import { Search, SentimentDissatisfied } from "@mui/icons-material";
import {
  CircularProgress,
  Grid,
  InputAdornment,
  TextField,
} from "@mui/material";
import { Box } from "@mui/system";
import axios from "axios";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { config } from "../App";
import Footer from "./Footer";
import Header from "./Header";
import "./Products.css";
import ProductCard from "./ProductCard";
import Cart, { generateCartItemsFrom } from "./Cart";
import { fetchCart } from "./Checkout";

/**
 * @typedef {Object} CartItem -  - Data on product added to cart
 * 
 * @property {string} name - The name or title of the product in cart
 * @property {string} qty - The quantity of product added to cart
 * @property {string} category - The category that the product belongs to
 * @property {number} cost - The price to buy the product
 * @property {number} rating - The aggregate rating of the product (integer out of five)
 * @property {string} image - Contains URL for the product image
 * @property {string} productId - Unique ID for the product
 */

const Products = () => {
  const [items, setItems] = useState([]);
  const { enqueueSnackbar } = useSnackbar();
  const [debounceTimeout, setDebounceTimeout] = useState(0);
  const [isLoading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const token = localStorage.getItem("token");

  /**
   * Make API call to get the products list and store it to display the products
   */
  const performAPICall = async () => {
    setLoading(true);

    try {
      const response = await axios.get(`${config.endpoint}/products`);

      setLoading(false);
      setProducts(response.data);
      setFilteredProducts(response.data);
      return response.data;
    } catch (e) {
      setLoading(false);
      enqueueSnackbar(
        "Could not fetch products. Check that the backend is running, reachable, and returns valid JSON.",
        { variant: "error" }
      );
      return null;
    }
  };

  /**
   * Search handler
   */
  const performSearch = async (text) => {
    try {
      const response = await axios.get(
        `${config.endpoint}/products/search?value=${text}`
      );
      setFilteredProducts(response.data);
    } catch (e) {
      if (e.response && e.response.status === 404) {
        setFilteredProducts([]);
      } else {
        enqueueSnackbar("Could not fetch products. Please try again later.", {
          variant: "error",
        });
      }
    }
  };

  /**
   * Debounce handler for search
   */
  const debounceSearch = (event) => {
    const value = event.target.value;
    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }
    const newTimeout = setTimeout(() => {
      performSearch(value);
    }, 500);
    setDebounceTimeout(newTimeout);
  };

  /**
   * Update cart items
   */
  const updateCartItems = (cartData, products) => {
    const cartItems = generateCartItemsFrom(cartData, products);
    setItems(cartItems);
  };

  /**
   * Add to Cart functionality
   */
  const addToCart = async (token, items, products, productId, qty, options = { preventDuplicate: false }) => {
    if (!token) {
      enqueueSnackbar("Login to add an item to the Cart", { variant: "warning" });
      return;
    }

    // Check if the product already exists in the cart
    const productInCart = items.find(item => item.productId === productId);
    if (productInCart && options.preventDuplicate) {
      enqueueSnackbar("Item already in cart. Use the cart sidebar to update quantity or remove item.", { variant: "warning" });
      return;
    }

    try {
      // Make the API call to add/update the product in the cart
      const response = await axios.post(`${config.endpoint}/cart`, {
        productId: productId,
        qty: qty
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Update the local cart items with the new data
      const updatedCartItems = generateCartItemsFrom(response.data, products);
      setItems(updatedCartItems);
    } catch (e) {
      enqueueSnackbar("Could not update cart. Please try again later.", { variant: "error" });
    }
  };

  /**
   * Fetch products and cart on page load
   */
  useEffect(() => {
    const onLoadHandler = async () => {
      const productsData = await performAPICall();
      const cartData = await fetchCart(token); // Define fetchCart similarly to fetch cart data
      const cartDetails = generateCartItemsFrom(cartData, productsData);
      setItems(cartDetails);
    };
    onLoadHandler();
  }, [token]);

  return (
    <div>
      <Header>
        <TextField
          className="search-desktop"
          size="small"
          InputProps={{
            className: "search",
            endAdornment: (
              <InputAdornment position="end">
                <Search color="primary" />
              </InputAdornment>
            ),
          }}
          placeholder="Search for items/categories"
          name="search"
          onChange={debounceSearch}
        />
      </Header>

      <TextField
        className="search-mobile"
        size="small"
        fullWidth
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Search color="primary" />
            </InputAdornment>
          ),
        }}
        placeholder="Search for items/categories"
        name="search"
        onChange={debounceSearch}
      />

      <Grid container>
        <Grid
          item
          xs={12}
          md={token && products.length ? 9 : 12}
          className="product-grid"
        >
          <Box className="hero">
            <p className="hero-heading">
              India’s <span className="hero-highlight">FASTEST DELIVERY</span>{" "}
              to your door step
            </p>
          </Box>

          {isLoading ? (
            <Box className="loading">
              <CircularProgress />
              <h4>Loading Products...</h4>
            </Box>
          ) : (
            <Grid container marginY="1rem" paddingX="1rem" spacing={2}>
              {filteredProducts.length ? (
                filteredProducts.map((product) => (
                  <Grid item xs={6} md={3} key={product._id}>
                    <ProductCard
                      product={product}
                      handleAddToCart={() =>
                        addToCart(token, items, products, product._id, 1, {
                          preventDuplicate: true,
                        })
                      }
                    />
                  </Grid>
                ))
              ) : (
                <Box className="loading">
                  <SentimentDissatisfied color="action" />
                  <h4 style={{ color: "#636363" }}>No products found</h4>
                </Box>
              )}
            </Grid>
          )}
        </Grid>
        {token ? (
          <Grid item xs={12} md={3} bgcolor="#E9F5E1">
            <Cart
              hasCheckoutButton
              products={products}
              items={items}
              handleQuantity={addToCart}
            />
          </Grid>
        ) : null}
      </Grid>
      <Footer />
    </div>
  );
};

export default Products;